import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Campaign, getError, useToast } from '@aslanpay/portal-common-lib';

import useDeleteCampaign from 'app/hooks/useDeleteCampaign';
import { companyPaths } from 'app/constants/routes';

interface DeleteCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
  companyId: string;
}

/**
 * Deleting a campaign that never ran.
 * <p>
 * The service refuses one whose scheme has ever started, and one whose pot still holds points. Both are
 * said in its own words rather than guessed at, since either can become true between the page loading and
 * the button being pressed.
 */
function DeleteCampaignModal({ isOpen, onClose, campaign, companyId }: DeleteCampaignModalProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: remove, isLoading } = useDeleteCampaign({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign deleted',
        description: `${campaign.name} has been removed.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
      onClose();
      navigate(generatePath(companyPaths.campaigns.path, { companyId }));
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not delete the campaign',
        description: getError(error).message || 'Nothing was changed. Please try again.',
      }),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="delete-campaign-modal">
        <ModalHeader>Delete this campaign?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="12px">
            <Text textStyle="smallRegular" color="brand.black">
              {campaign.name} and every pride beneath it are removed. This cannot be undone.
            </Text>
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              A campaign that has ever run is cancelled rather than deleted, so its nominations and
              winners are kept. One whose pot still holds points cannot be deleted until they are
              moved to another pot.
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={onClose} data-testid="keep-campaign-instead">
            Keep it
          </Button>
          <Button
            colorScheme="red"
            onClick={() => remove(campaign.id)}
            isLoading={isLoading}
            data-testid="confirm-delete-campaign"
          >
            Delete campaign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DeleteCampaignModal;
